import React, { useState } from 'react';
import { Phone, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ProtectedPhoneProps {
  className?: string;
  showIcon?: boolean;
  variant?: 'button' | 'link' | 'text';
  size?: 'sm' | 'md' | 'lg';
  phoneNumber?: string;
}

const ProtectedPhone: React.FC<ProtectedPhoneProps> = ({ 
  className = '', 
  showIcon = true, 
  variant = 'button',
  size = 'md',
  phoneNumber = '+41 76 622 51 17'
}) => {
  const { i18n } = useTranslation();
  const [revealed, setRevealed] = useState(false);
  const isGerman = i18n.language === 'de';
  
  const handleClick = () => {
    setRevealed(true);
    // Small delay to show the phone number before opening tel:
    setTimeout(() => {
      window.location.href = `tel:${phoneNumber.replace(/\s/g, '')}`;
    }, 100);
  };

  const sizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg'
  };

  const variantClasses = {
    button: 'inline-flex items-center px-6 py-3 bg-slate-100 text-slate-900 hover:bg-slate-200 transition-all cursor-pointer min-h-[44px] font-medium',
    link: 'text-accent-orange hover:text-accent-orange-dark transition-colors cursor-pointer underline',
    text: 'text-gray-700 hover:text-accent-orange transition-colors cursor-pointer'
  };

  if (revealed) {
    return (
      <span className={`${sizeClasses[size]} ${className} inline-flex items-center`}>
        {showIcon && <Phone className="w-4 h-4 mr-2 inline" />}
        {phoneNumber}
        <ExternalLink className="w-3 h-3 ml-1 inline" />
      </span>
    );
  }

  return (
    <span 
      className={`${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      title={isGerman ? 'Klicken Sie, um die Telefonnummer anzuzeigen' : 'Click to reveal phone number'}
    >
      {showIcon && <Phone className="w-4 h-4 mr-2 inline" />}
      {isGerman ? 'Telefon anzeigen' : 'Show Phone'}
    </span>
  );
};

export default ProtectedPhone;

